//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

// TODO: PD-253779 - Convert from Angular to React component
import { FeatureNames } from '@bvengineering/curalate-features';
import angular, {
  IHttpService,
  IPromise,
  IRootElementService,
  ITimeoutService,
  IWindowService,
} from 'angular';
import { IStateService } from 'angular-ui-router';
import { INgRedux } from 'ng-redux';
import _ from 'underscore';

import { isInsightsPageVisible } from '@insights/redux/selectors/getAvailableInsightsPages';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { IToastNotificationWithId } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { NAV_ACTIVE_REGEXES } from '@legacy-components/nav3/navigation-menus/constants/NavRegex';
import { ICuralateStore } from '@models/AppState';
import { SessionVarsService } from '@services/session-vars-service';
import getKeys from '@utils/getKeys';

import template from 'app/components/nav3/top-navigation-refreshed.html';
import { GaApi } from 'app/initialization/ga/ga-api';

import '@instagram-post-uploader/instagram-post-uploader';
import '@legacy-components/common/toast-notifications/toast-notifications';

import { BoundActionCreatorMap } from 'redux-extensions';
import 'app/components/settings-menu/settings-menu';
import 'app/components/nav3/ads-menu';
import 'app/components/nav3/app-switcher-menu';
import 'app/components/nav3/catalog-menu';
import 'app/components/nav3/creators-menu';
import 'app/components/nav3/galleries-menu';
import 'app/components/nav3/help-menu';
import 'app/components/nav3/influencers-menu';
import 'app/components/nav3/media-menu';
import 'app/components/nav3/nav3-menu-item';
import 'app/components/nav3/nav3-subsection';
import 'app/components/nav3/social-menu';
import 'app/components/nav3/publishing-menu';
import 'app/components/nav3/storefronts-menu';
import 'app/components/nav3/nav-logo';
import 'app/components/nav3/navigation-menus/NavMenuTabs';

export interface TopNavigationRefreshed
  extends ReturnType<(typeof TopNavigationRefreshed)['mapStateToThis']>,
    BoundActionCreatorMap<typeof toastNotificationsActions> {}

export class TopNavigationRefreshed {
  private static readonly mapStateToThis = (state: ICuralateStore) => ({
    isInsightsPageVisible: isInsightsPageVisible(state),
    isInstagramPostUploaderModalOpen: state.instagramPostUploader.isModalOpen,
    toastNotifications: state.notifications.toastNotifications,
    contentDrawerSettings: state.contentDrawer.drawerSettings,
    toastNotificationsToClose: state.notifications.toastNotificationsToClose,
  });

  private readonly areSocialDashboardsEnabled: boolean;
  private readonly isBvPortalQuickLinkEnabled: boolean;
  private readonly isFacebookReview: boolean;
  private readonly isShareEnabled: boolean;
  private readonly isInfluencersDiscoveryEnabled: boolean;
  private readonly isGoogleAssetFeedsEnabled: boolean;
  private readonly isGalleriesEnabled: boolean;
  private readonly isInstashopEnabled: boolean;
  private readonly isEmailBuilderEnabled: boolean;
  private readonly isBazaarvoiceSyndicationEnabled: boolean;
  private readonly isBazaarvoiceUgcSyndicationEnabled: boolean;
  private readonly isSocialResponseEnabled: boolean;
  private readonly isListeningEnabled: boolean;
  private readonly isDropboxImportEnabled: boolean;

  private readonly isCommerceEnabled: boolean;
  private readonly isInstagramShopsUgcEnabled: boolean;
  private readonly isInstagramAdsContentAllowlistingEnabled: boolean;
  private readonly isLike2BuyEnabled: boolean;
  private readonly isLike2BuyV2Enabled: boolean;
  private readonly isLike2BuyTiktokEnabled: boolean;
  private readonly isPaidSocialVisible: boolean;
  private readonly isPromotedPostsEnabled: boolean;
  private readonly isRevealPageEnabled: boolean;
  private readonly isSocialVisible: boolean;
  private readonly isTiktokSparkAdsEnabled: boolean;
  private readonly isTributarySelfServeEnabled: boolean;
  private readonly isVisualSyndicationVisible: boolean;

  // TODO: PD-229241 - Remove feature flag usage once Harmony AI Creators Integration is fully released
  private readonly isHarmonyAiCreatorsIntegrationFeatureEnabled: boolean;
  private readonly isCreatorCommunityEnabled: boolean;
  private readonly isInfluensterCampaignsEnabled: boolean;
  private readonly isMediaLibraryFeatureEnabled: boolean;
  private readonly isNotificationsDeprecated: boolean;
  private readonly openMenus = new Set();
  private menuViewedTimeouts: Record<string, IPromise<void>> = {};
  private readonly unsubscribe: () => void;

  constructor(
    private readonly $ngRedux: INgRedux,
    private readonly $element: IRootElementService,
    private readonly $http: IHttpService,
    private readonly $state: IStateService,
    private readonly $timeout: ITimeoutService,
    private readonly $window: IWindowService,
    private readonly sessionVarsService: SessionVarsService,
    private readonly TrackingService: GaApi,
  ) {
    this.unsubscribe = $ngRedux.connect(
      TopNavigationRefreshed.mapStateToThis,
      toastNotificationsActions,
    )(this);

    this.areSocialDashboardsEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.SocialDashboards,
    );
    this.isBvPortalQuickLinkEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.BvPortalQuickLink,
    );
    this.isFacebookReview = this.sessionVarsService.isFacebookReview();
    this.isShareEnabled =
      this.sessionVarsService.isFeatureEnabled(FeatureNames.Syndication) ||
      this.sessionVarsService.isSyndicationClient();

    // TODO: PD-239417 Remove feature flag after GA
    this.isSocialResponseEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.SocialResponse,
    );

    this.isListeningEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.SocialListening,
    );

    this.isInfluencersDiscoveryEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.InfluencersDiscovery,
      );
    this.isGoogleAssetFeedsEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.AssetFeedTagging,
    );
    this.isGalleriesEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.Galleries,
    );
    this.isInstashopEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.Instashop,
    );
    this.isEmailBuilderEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.EmailBuilder,
    );
    this.isBazaarvoiceSyndicationEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.BazaarvoiceNetworkSyndicationFrontend,
      );
    this.isHarmonyAiCreatorsIntegrationFeatureEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.HarmonyAiCreatorsIntegration,
      );
    this.isInfluensterCampaignsEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.InfluensterCampaigns,
      );
    this.isMediaLibraryFeatureEnabled =
      this.sessionVarsService.isFeatureEnabled(FeatureNames.MediaLibraryPage);

    this.isCreatorCommunityEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.CreatorCommunity,
    );
    this.isNotificationsDeprecated = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.NotificationsUiDeprecation,
    );
    this.isDropboxImportEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.DamImportDropbox,
    );
    this.isRevealPageEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.RevealPage,
    );
    this.isInstagramAdsContentAllowlistingEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.InstagramAdsContentAllowlisting,
      );
    this.isPromotedPostsEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.PromotedPosts,
    );
    this.isTiktokSparkAdsEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.TiktokSparkAds,
    );
    this.isLike2BuyEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.Like2Buy,
    );
    this.isLike2BuyV2Enabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.InstagramLike2buyV2,
    );
    this.isLike2BuyTiktokEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.Like2BuyTiktok,
    );
    this.isTributarySelfServeEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.TributarySelfServe,
    );
    this.isInstagramShopsUgcEnabled = this.sessionVarsService.isFeatureEnabled(
      FeatureNames.InstagramShopsUgc,
    );
    this.isBazaarvoiceUgcSyndicationEnabled =
      this.sessionVarsService.isFeatureEnabled(
        FeatureNames.BazaarvoiceNetworkSyndicationUgc,
      );

    this.isSocialVisible =
      this.isLike2BuyEnabled ||
      this.isLike2BuyV2Enabled ||
      this.isLike2BuyTiktokEnabled ||
      this.isTributarySelfServeEnabled ||
      this.isInstagramShopsUgcEnabled;

    this.isPaidSocialVisible =
      this.isInstagramAdsContentAllowlistingEnabled ||
      this.isPromotedPostsEnabled ||
      this.isTiktokSparkAdsEnabled;

    this.isVisualSyndicationVisible =
      this.isBazaarvoiceSyndicationEnabled && !this.isInstashopEnabled;

    this.isCommerceEnabled =
      this.isSocialVisible ||
      this.isVisualSyndicationVisible ||
      this.isGalleriesEnabled ||
      this.isPaidSocialVisible ||
      this.isEmailBuilderEnabled ||
      this.isRevealPageEnabled ||
      this.isGoogleAssetFeedsEnabled;
  }

  public $onDestroy() {
    this.unsubscribe();
  }

  public isSyndicationClient = () => {
    // Some elements in the top-nav are hidden in Share 1.0
    return this.sessionVarsService.isSyndicationClient();
  };

  public isLegacySyndicationOrConnectClient = () => {
    return (
      (this.sessionVarsService.isSyndicationClient() ||
        this.sessionVarsService.isShareV2VendorClient()) &&
      !this.isBazaarvoiceSyndicationEnabled
    );
  };

  public isBazaarvoiceSyndicationOnlyClient = () => {
    return (
      (this.sessionVarsService.isSyndicationClient() ||
        this.sessionVarsService.isShareV2VendorClient()) &&
      this.isBazaarvoiceSyndicationEnabled &&
      !this.isGalleriesEnabled &&
      !this.isBazaarvoiceUgcSyndicationEnabled
    );
  };

  public isCuralateEmployee = () =>
    this.sessionVarsService.isCuralateEmployee();

  public activeRoute = () =>
    (typeof this.$state.current.data?.navGroup === 'function'
      ? this.$state.current.data?.navGroup()
      : this.$state.current.data?.navGroup) || this.calculateActiveRoute();

  public onToastNotificationNotified(notification: IToastNotificationWithId) {
    this.removeToastNotification(notification.id);
  }

  public onToastNotificationClosed(notificationId: string) {
    this.removeClosedToastNotification(notificationId);
  }

  public menuOpened = (name: string) => {
    this.openMenus.add(name);

    this.$timeout.cancel(this.menuViewedTimeouts[name]);

    this.menuViewedTimeouts[name] = this.$timeout(() => {
      this.TrackingService.recordEvent(
        this.TrackingService.CATEGORY_NAVIGATION3,
        this.TrackingService.ACTION_NAVIGATION3_TOP_NAV_HOVER,
        name,
      );
    }, 500);
  };

  public menuClosed = (name: string) => {
    this.$timeout.cancel(this.menuViewedTimeouts[name]);
    this.openMenus.delete(name);
  };

  private calculateActiveRoute() {
    const currentPath = this.$window.location.pathname;
    return getKeys(NAV_ACTIVE_REGEXES).find((key) =>
      NAV_ACTIVE_REGEXES[key].test(currentPath),
    );
  }
}

angular.module('curalate').component('topNavigationRefreshed', {
  template,
  bindings: {},
  controller: [
    '$ngRedux',
    '$element',
    '$http',
    '$state',
    '$timeout',
    '$window',
    'SessionVarsService',
    'TrackingService',
    TopNavigationRefreshed,
  ],
});
